import i18next from 'i18next'
import * as React from 'react'
import { Form, FormControl, Spinner } from 'react-bootstrap'
import { Link } from 'widgets-for-react'
import { ButtonPrimary, ButtonSecondary } from './buttons'
import {
  NestedValidatorResult,
  SingleValidatorResult,
} from '../validator/validatorExpr'
import { PasswordScoreTypes } from '../authentication/auth_widget/authenticationApi'
import {
  LoginFlowClasses,
  LoginFlowStyle,
} from '../authentication/auth_widget/style'
import { getClassName, options } from '../authentication/auth_widget/utils'
import { GroupBase } from 'react-select'
import AsyncSelect, { AsyncProps } from 'react-select/async'

// ==========================================================================
// FORM SKELETON
// ==========================================================================

export function getFSClassName(
  className: keyof LoginFlowStyle,
  notset: string = ''
): string {
  return getClassName(formSkeletonConfig.options, className, notset)
}

// ----- CONFIG

export const formSkeletonConfig = {
  rootUrl: '/',
  logoSrc: '/spa/images/logo-black.svg',
  logoSrcInverted: '/spa/images/logo-white.svg',
  sloganClassName: 'title--m',
  useCustomButtonHTML: true,
  options: { style: LoginFlowClasses },
  imageLight: {
    src: '/spa/images/fennel-sous-vide-small.jpg',
    srcSet:
      '/spa/images/fennel-sous-vide-small.jpg 480w, /spa/images/fennel-sous-vide-medium.jpg 960w, /spa/images/fennel-sous-vide-large.jpg 1920w',
  },
  imageDark: {
    src: '/spa/images/romanoff-small.jpg',
    srcSet:
      '/spa/images/romanoff-small.jpg 480w, /spa/images/romanoff-medium.jpg 960w, /spa/images/romanoff-large.jpg 1920w',
  },
}

// ----- STRUCTURE

export type FormTypes =
  | 'sign-up'
  | 'sign-up-succes'
  | 'login'
  | 'reset-password-request'
  | 'reset-password-request-success'
  | 'reset-password'
  | 'reset-password-success'
  | 'resend-activation'
  | 'resend-activation-success'
  | 'trial-to-pro'
  | 'trial-to-pro-success'
  | 'trial-to-pro-failed'
  | 'redirect-to-psp'
  | 'activate-account-failed'
  | 'student-subscription'

export type FormSkeletonProps = {
  type: FormTypes
  children: React.ReactNode
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
  options: options
  slogan?: string
  isClrInverted?: boolean
  modifier?: string
  rootUrl?: string
} & (
  | { kind: 'text' }
  | { kind: 'text-image'; image: { src: string; srcSet?: string } }
)

export const FormSkeletonLayout = (p: FormSkeletonProps) => {
  const {
    children,
    isClrInverted,
    kind,
    modifier,
    onSubmit,
    slogan,
    type,
    options,
    rootUrl,
  } = p
  return (
    <div
      key={`auth-${type}-form`}
      className={`auth auth--${type} auth--${kind}${
        modifier ? ` auth--${modifier}` : ''
      }${isClrInverted ? ' auth--clr-inverted' : ''}`}
    >
      <header className="auth__header">
        <div className="auth__header-inner">
          <Link
            to={rootUrl || formSkeletonConfig.rootUrl}
            className="auth__logo"
            title={i18next.t('a11y:navigateHome')}
          >
            <span className="sr-text">{i18next.t('a11y:navigateHome')}</span>
            <img
              src={
                isClrInverted
                  ? formSkeletonConfig.logoSrcInverted
                  : formSkeletonConfig.logoSrc
              }
              alt=""
            />
          </Link>

          {slogan && (
            <p
              className={`auth__slogan ${
                formSkeletonConfig.sloganClassName || ''
              }`}
            >
              {slogan}
            </p>
          )}
        </div>
        {p.kind === 'text-image' && p.image ? (
          <div className="auth__visual" aria-hidden="true">
            <img
              className="auth__image lazyload"
              alt=""
              src="/spa/images/empty.png"
              data-src={p.image.src || ''}
              data-srcset={p.image.srcSet || ''}
              data-sizes="auto"
            />
          </div>
        ) : (
          <div className="auth__visual" aria-hidden="true" />
        )}
      </header>

      <main
        id="auth__main"
        className={`auth__main ${getFSClassName(
          'blockClassName',
          'container'
        )}`}
      >
        {onSubmit !== undefined ? (
          <form
            onSubmit={(e) => onSubmit(e)}
            className={`auth__form ${getFSClassName('formClassName')}`}
          >
            <div
              className={`auth__form-inner ${getFSClassName(
                'formInnerClassName'
              )}`}
            >
              {children}
            </div>
          </form>
        ) : (
          <div className={`auth__form ${getFSClassName('formClassName')}`}>
            <div
              className={`auth__form-inner ${getFSClassName(
                'formInnerClassName'
              )}`}
            >
              {children}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

// ==========================================================================
// FORM CONTROLS
// ==========================================================================

type FormItemBaseProps = {
  label: string | JSX.Element
  hideLabel?: boolean
  controlId: string
  readOnly?: boolean
  required: boolean
  fieldMessage?: string
}

type FormControlProps = {
  type?: string
  rows?: number
  as?: React.ElementType
  value?: string
  onChange: (_: string) => void
  onBlur?: () => void
  validation?: SingleValidatorResult | NestedValidatorResult
  children?: JSX.Element | Array<JSX.Element>
  placeholder?: string
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search'
  showPasswordToggle?: {
    isChecked: boolean
    label: string
    title?: string
    onChange: (_: boolean) => void
  }

  passwordScore?: PasswordScoreTypes
  maxLength?: number
  suppressHotjar?: boolean
} & FormItemBaseProps

type FormSelectProps = {
  value: string
  onChange: (_: string) => void
  onBlur?: () => void
  validation?: SingleValidatorResult | NestedValidatorResult
  children?: JSX.Element | Array<JSX.Element>
  suppressHotjar?: boolean
} & FormItemBaseProps

type FormBooleanProps = {
  checked: boolean
  onChange: (_: boolean) => void
  onBlur?: () => void
  validation?: SingleValidatorResult
  suppressHotjar?: boolean
} & FormItemBaseProps

type FormRadioFieldsetItemProps = {
  label: string | JSX.Element
  controlId: string
  checked: boolean
  onChange: (_: boolean) => void
}

type FormRadioFieldsetProps = {
  legend: string
  name: string
  required: boolean
  readOnly?: boolean
  fieldMessage?: string
  items: FormRadioFieldsetItemProps[]
  validation: SingleValidatorResult | undefined
  suppressHotjar?: boolean
}

const fieldErrorClassName = () => getFSClassName('formFieldErrorClassName')

interface CustomSelectAsyncProps {
  label: string | JSX.Element
  required?: boolean
  controlId: string
  fieldMessage?: string
  validation?: SingleValidatorResult
  suppressHotjar?: boolean
}

export const FormControls = {
  ShowIf: (props: { condition: boolean; children: React.ReactNode }) =>
    props.condition ? <>{props.children}</> : <></>,
  Loader: (props: {}) => (
    <div className="d-flex flex-column-reverse align-items-center justify-content-center h-100">
      <Spinner animation="border" role="status" aria-hidden="true">
        <span className="sr-text">{i18next.t('global:Loading')}</span>
      </Spinner>
    </div>
  ),
  SubmittingSpinner: (props: {}) => (
    <Spinner animation="border" role="status" as="span" size="sm">
      <span className="sr-text">{i18next.t('global:Submitting')}</span>
    </Spinner>
  ),
  OptionalLabel: () => <span>{` ${i18next.t('auth:optional')}`}</span>,
  FormItemInGroup: (
    props: FormControlProps & { extraJSX?: React.ReactNode }
  ): JSX.Element => {
    const isInvalid = props.validation && props.validation.kind === 'failed'
    const hasErrorMessage =
      props.validation &&
      props.validation.kind === 'failed' &&
      props.validation.message
    const errorMessageId = `a11y-${props.controlId}-form-error`
    const hasFieldMessage = props.fieldMessage
    const fieldMessageId = `a11y-${props.controlId}-form-message`
    const passwordToggleId = `a11y-${props.controlId}-form-password-toggle`
    const isPasswordControl = props.controlId.toLowerCase().includes('password')
    const hasPasswordStrengthBar = props.passwordScore !== undefined
    const inputClassName = getFSClassName('formInputClassName')
    const selectClassName = getFSClassName('formSelectClassName')
    const FormControl = (
      <Form.Control
        type={props.type}
        as={props.as}
        rows={props.rows}
        value={props.value || ''}
        placeholder={props.placeholder}
        inputMode={props.inputMode || 'text'}
        readOnly={props.readOnly}
        disabled={props.readOnly}
        aria-required={props.required}
        isInvalid={isInvalid ? true : undefined}
        aria-invalid={isInvalid}
        aria-describedby={
          isInvalid ? errorMessageId : hasFieldMessage ? fieldMessageId : ''
        }
        onChange={(e) => props.onChange(e.currentTarget.value)}
        onBlur={() => {
          if (props.onBlur) props.onBlur()
        }}
        className={
          (props.as === 'select' ? selectClassName : inputClassName) +
          (isInvalid ? ` ` : '')
        }
        maxLength={props.maxLength}
      >
        {props.children}
      </Form.Control>
    )
    return (
      <Form.Group
        controlId={props.controlId}
        className={`${getFSClassName('formGroupClassName')}${
          props.suppressHotjar == true ? ' data-hj-suppress' : ''
        }`}
      >
        <Form.Label
          className={
            props.hideLabel ? 'sr-text' : getFSClassName('formLabelClassName')
          }
        >
          {props.label}
          {!props.required && <FormControls.OptionalLabel />}
        </Form.Label>

        {/* Add wrapper around input with showPasswordToggle, otherwise showPasswordToggle positioning conflicts with the field validation */}
        {isPasswordControl && props.showPasswordToggle ? (
          // Add error classes to input-wrapper too, because field validation is shown through CSS sibling selector (.is-invalid ~ .invalid-feedback)
          <div
            className={`lf__input-wrapper${
              hasErrorMessage && !hasPasswordStrengthBar ? ' is-invalid' : ''
            }`}
          >
            {FormControl}

            {/* Password toggle */}
            <div className="lf__password-toggle">
              <input
                type="checkbox"
                id={passwordToggleId}
                checked={props.showPasswordToggle.isChecked}
                disabled={props.readOnly}
                onChange={(e) =>
                  props?.showPasswordToggle?.onChange(e.currentTarget.checked)
                }
              />
              <label
                htmlFor={passwordToggleId}
                title={props.showPasswordToggle.title}
              >
                <span>{props.showPasswordToggle.label}</span>
              </label>
            </div>
          </div>
        ) : (
          <>{FormControl}</>
        )}

        {/* Validation feedback */}
        {hasErrorMessage && !hasPasswordStrengthBar && (
          <Form.Control.Feedback
            as="p"
            role="alert"
            type="invalid"
            id={errorMessageId}
            className={getFSClassName('formFieldFeedbackInvalidClassName')}
          >
            {hasErrorMessage}
          </Form.Control.Feedback>
        )}

        {hasFieldMessage && (
          <Form.Text
            as="p"
            id={fieldMessageId}
            className={getFSClassName('formFieldFeedbackClassName')}
          >
            {hasFieldMessage}
          </Form.Text>
        )}

        {/* Password strength bar */}
        {hasPasswordStrengthBar && props.passwordScore && (
          <FormControls.PasswordStrengthBar
            password={props.value}
            passwordScore={props.passwordScore}
            errorMessageId={errorMessageId}
          />
        )}

        {/* Extra JSX */}
        {props.extraJSX}
      </Form.Group>
    )
  },
  FormSelectInGroup: (props: FormSelectProps): JSX.Element => {
    const isInvalid = props.validation && props.validation.kind === 'failed'
    const hasErrorMessage =
      props.validation &&
      props.validation.kind === 'failed' &&
      props.validation.message
    const errorMessageId = `a11y-${props.controlId}-form-error`
    const hasFieldMessage = props.fieldMessage
    const fieldMessageId = `a11y-${props.controlId}-form-message`
    const selectClassName = getFSClassName('formSelectClassName')
    return (
      <Form.Group
        controlId={props.controlId}
        className={`${getFSClassName('formGroupClassName')}${
          props.suppressHotjar == true ? ' data-hj-suppress' : ''
        }`}
      >
        <Form.Label
          className={
            props.hideLabel ? 'sr-text' : getFSClassName('formLabelClassName')
          }
        >
          {props.label}
          {!props.required && <FormControls.OptionalLabel />}
        </Form.Label>

        <Form.Select
          value={props.value || ''}
          disabled={props.readOnly}
          aria-required={props.required}
          isInvalid={isInvalid ? true : undefined}
          aria-invalid={isInvalid}
          aria-describedby={
            isInvalid ? errorMessageId : hasFieldMessage ? fieldMessageId : ''
          }
          onChange={(e) => props.onChange(e.currentTarget.value)}
          onBlur={() => {
            if (props.onBlur) props.onBlur()
          }}
          className={selectClassName}
        >
          {props.children}
        </Form.Select>

        {/* Validation feedback */}
        {hasErrorMessage && (
          <Form.Control.Feedback
            as="p"
            role="alert"
            type="invalid"
            id={errorMessageId}
            className={getFSClassName('formFieldFeedbackInvalidClassName')}
          >
            {hasErrorMessage}
          </Form.Control.Feedback>
        )}

        {hasFieldMessage && (
          <Form.Text
            as="p"
            id={fieldMessageId}
            className={getFSClassName('formFieldFeedbackClassName')}
          >
            {hasFieldMessage}
          </Form.Text>
        )}
      </Form.Group>
    )
  },
  FormAsyncSelectInGroup: <
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
  >({
    ...props
  }: AsyncProps<Option, IsMulti, Group> &
    CustomSelectAsyncProps): JSX.Element => {
    const isInvalid = props.validation && props.validation.kind === 'failed'
    const hasErrorMessage =
      props.validation &&
      props.validation.kind === 'failed' &&
      props.validation.message
    const errorMessageId = `a11y-${props.controlId}-form-error`
    const hasFieldMessage = props.fieldMessage
    const fieldMessageId = `a11y-${props.controlId}-form-message`

    return (
      <Form.Group
        controlId={props.controlId}
        className={`${getFSClassName('formGroupClassName')}${
          props.suppressHotjar == true ? ' data-hj-suppress' : ''
        }`}
      >
        <Form.Label className={getFSClassName('formLabelClassName')}>
          {props.label}
          {!props.required && <FormControls.OptionalLabel />}
        </Form.Label>

        <AsyncSelect
          {...props}
          noOptionsMessage={({ inputValue }) =>
            inputValue == ''
              ? i18next.t('common:startTyping')
              : i18next.t('common:noResultsFound')
          }
          placeholder=""
          isClearable
          styles={{
            menu: (styles) => ({ ...styles, zIndex: 999 }),
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderColor: state.isFocused ? '#FF4C07' : '#DAD5D4',
              boxShadow: 'none',
              '&:hover': {
                borderColor: '#FF4C07',
                boxShadow: 'none',
              },
            }),
          }}
        />

        {hasErrorMessage && (
          <Form.Control.Feedback
            type="invalid"
            id={errorMessageId}
            className={getFSClassName('formFieldFeedbackInvalidClassName')}
          >
            {hasErrorMessage}
          </Form.Control.Feedback>
        )}

        {hasFieldMessage && (
          <Form.Text
            as="p"
            id={fieldMessageId}
            className={getFSClassName('formFieldFeedbackClassName')}
          >
            {hasFieldMessage}
          </Form.Text>
        )}
      </Form.Group>
    )
  },
  FormCheckInGroup: (props: FormBooleanProps): JSX.Element => {
    const isInvalid = props.validation && props.validation.kind === 'failed'
    const hasErrorMessage =
      props.validation &&
      props.validation.kind === 'failed' &&
      props.validation.message
    const errorMessageId = `a11y-${props.controlId}-form-error`
    const hasFieldMessage = props.fieldMessage
    const fieldMessageId = `a11y-${props.controlId}-form-message`
    const checkboxClassName = getFSClassName('formBooleanInputClassName')
    const checkboxLabelClassName = getFSClassName('formBooleanLabelClassName')
    return (
      <Form.Group
        controlId={props.controlId}
        className={`${getFSClassName('formGroupClassName', 'form-group')}${
          props.suppressHotjar == true ? ' data-hj-suppress' : ''
        }`}
      >
        <div className={`form-check ${isInvalid ? fieldErrorClassName() : ''}`}>
          <Form.Check.Input
            type="checkbox"
            readOnly={props.readOnly}
            disabled={props.readOnly}
            checked={props.checked}
            isInvalid={isInvalid ? true : undefined}
            aria-invalid={isInvalid}
            aria-describedby={
              isInvalid ? errorMessageId : hasFieldMessage ? fieldMessageId : ''
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              props.onChange(e.currentTarget.checked)
            }
            onBlur={() => {
              if (props.onBlur) props.onBlur()
            }}
            className={checkboxClassName + (isInvalid ? ` ` : '')}
          />
          {typeof props.label === 'string' ? (
            <Form.Check.Label
              className={checkboxLabelClassName}
              htmlFor={props.controlId}
              dangerouslySetInnerHTML={{ __html: props.label }}
            />
          ) : (
            <Form.Check.Label
              className={checkboxLabelClassName}
              htmlFor={props.controlId}
            >
              {props.label}
              {!props.required && <FormControls.OptionalLabel />}
            </Form.Check.Label>
          )}
        </div>

        {hasErrorMessage && (
          <Form.Control.Feedback
            type="invalid"
            id={errorMessageId}
            className={getFSClassName('formFieldFeedbackInvalidClassName')}
          >
            {hasErrorMessage}
          </Form.Control.Feedback>
        )}

        {hasFieldMessage && (
          <Form.Text
            as="p"
            id={fieldMessageId}
            className={getFSClassName('formFieldFeedbackClassName')}
          >
            {hasFieldMessage}
          </Form.Text>
        )}
      </Form.Group>
    )
  },
  FormRadioInGroup: (props: FormRadioFieldsetProps): JSX.Element => {
    const isInvalid = props.validation && props.validation.kind === 'failed'
    const hasErrorMessage =
      props.validation &&
      props.validation.kind === 'failed' &&
      props.validation.message
    const errorMessageId = `a11y-${props.name}-form-error`
    const hasFieldMessage = props.fieldMessage
    const fieldMessageId = `a11y-${props.name}-form-message`
    const radioClassName = getFSClassName('formBooleanInputClassName')
    const radioLabelClassName = getFSClassName('formBooleanLabelClassName')
    return (
      <fieldset
        className={`${getFSClassName('formGroupClassName')} ${
          props.suppressHotjar == true ? 'data-hj-suppress' : ''
        }`}
      >
        <Form.Label as="legend" className={getFSClassName('formLabelClassName')}>
          {props.legend}
          {!props.required && <FormControls.OptionalLabel />}
        </Form.Label>
        {props.items.length > 0 &&
          props.items.map((item) => (
            <div
              className={`form-check ${isInvalid ? fieldErrorClassName() : ''}`}
              key={item.controlId}
            >
              <Form.Check.Input
                type="radio"
                name={props.name}
                id={item.controlId}
                readOnly={props.readOnly}
                disabled={props.readOnly}
                checked={item.checked}
                isInvalid={isInvalid ? true : undefined}
                aria-invalid={isInvalid}
                aria-describedby={
                  isInvalid
                    ? errorMessageId
                    : hasFieldMessage
                    ? fieldMessageId
                    : ''
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  item.onChange(e.currentTarget.checked)
                }
                className={radioClassName + (isInvalid ? ` ` : '')}
              />
              <Form.Check.Label
                className={radioLabelClassName}
                htmlFor={item.controlId}
              >
                {item.label}
              </Form.Check.Label>
            </div>
          ))}

        {hasErrorMessage && (
          <Form.Control.Feedback
            as="p"
            role="alert"
            type="invalid"
            id={errorMessageId}
            className={getFSClassName('formFieldFeedbackInvalidClassName')}
          >
            {hasErrorMessage}
          </Form.Control.Feedback>
        )}

        {hasFieldMessage && (
          <Form.Text
            as="p"
            id={fieldMessageId}
            className={getFSClassName('formFieldFeedbackClassName')}
          >
            {hasFieldMessage}
          </Form.Text>
        )}
      </fieldset>
    )
  },
  PasswordStrengthBar: (props: {
    password?: string
    passwordScore: PasswordScoreTypes
    errorMessageId: string
  }) => {
    const passwordStrengthText = (
      score: PasswordScoreTypes | 'short' | 'none'
    ): string => {
      switch (score) {
        case 'none':
          return ''
        case 'short':
          return i18next.t('auth:tooShort')
        case 'weak':
          return i18next.t('auth:weak')
        case 'almost good':
          return i18next.t('auth:almostGood')
        case 'strong':
          return i18next.t('auth:strong')
      }
    }

    const PasswordStrengthLayout = (
      score: PasswordScoreTypes | 'short' | 'none'
    ): JSX.Element => (
      <div
        className={`lf__pw-strength pw-strength--${score.replace(/\s+/g, '-')}`}
      >
        <div className="lf__pw-strength-inner">
          <span className="lf__pw-strength-meter" />
        </div>
        <p id={props.errorMessageId} className="lf__pw-strength-feedback">
          {score !== 'none' &&
            `${i18next.t('auth:passwordStrength')}: ${passwordStrengthText(
              score
            ).toLowerCase()}`}
        </p>
      </div>
    )

    return props.password == undefined
      ? PasswordStrengthLayout('none')
      : props.password.length < 6
      ? PasswordStrengthLayout('short')
      : PasswordStrengthLayout(props.passwordScore)
  },
  ReactRouterLink: (props: {
    path: string
    linkText: string
    target?: string
  }): JSX.Element => (
    <Link
      to={props.path}
      target={props.target}
      rel={props.target == '_blank' ? 'noopener noreferrer' : ''}
    >
      {props.linkText}
    </Link>
  ),
}

// ==========================================================================
// BUTTONS
// ==========================================================================

export type ClickableButtonProps = {
  kind: 'button'
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  type?: 'button' | 'submit'
}

export type ClickableLinkProps = {
  kind: 'link'
  to: string
  target?: string
  onClick?: () => void
}

export type ButtonProps = {
  label: string | JSX.Element
  disabled?: boolean
} & (ClickableButtonProps | ClickableLinkProps)

export const AuthButtonPrimary = (p: ButtonProps) => {
  return p.kind == 'button' ? (
    !formSkeletonConfig.useCustomButtonHTML ? (
      <button
        type={p.type}
        className={getFSClassName('clickablePrimaryClassName', 'btn btn-dark')}
        disabled={p.disabled}
        onClick={p.onClick}
      >
        <span className="button__label">{p.label}</span>
      </button>
    ) : (
      // ADD CUSTOM <button> HTML HERE
      <ButtonPrimary
        kind="button"
        size="s"
        type={p.type || 'button'}
        label={p.label}
        isDisabled={p.disabled}
        onClick={p.onClick}
      />
    )
  ) : !formSkeletonConfig.useCustomButtonHTML ? (
    <Link
      to={p.to}
      target={p.target}
      className={`${getFSClassName(
        'clickablePrimaryClassName',
        'btn btn-dark'
      )}${p.disabled ? ' disabled' : ''}`}
      onClick={p.onClick || undefined}
    >
      <span className="button__label">{p.label}</span>
    </Link>
  ) : (
    // ADD CUSTOM <Link> HTML HERE
    <ButtonPrimary
      kind="link"
      size="s"
      to={p.to}
      label={p.label}
      isDisabled={p.disabled}
      onClick={p.onClick}
    />
  )
}

export const AuthButtonSecondary = (p: ButtonProps) => {
  return p.kind == 'button' ? (
    !formSkeletonConfig.useCustomButtonHTML ? (
      <button
        type={p.type}
        className={getFSClassName(
          'clickableSecondaryClassName',
          'btn btn-dark'
        )}
        disabled={p.disabled}
        onClick={p.onClick}
      >
        <span className="button__label">{p.label}</span>
      </button>
    ) : (
      // ADD CUSTOM <button> HTML HERE
      <ButtonSecondary
        kind="button"
        size="s"
        type={p.type || 'button'}
        label={p.label}
        isDisabled={p.disabled}
        onClick={p.onClick}
      />
    )
  ) : !formSkeletonConfig.useCustomButtonHTML ? (
    <Link
      to={p.to}
      target={p.target}
      className={`${getFSClassName(
        'clickableSecondaryClassName',
        'btn btn-dark'
      )}${p.disabled ? ' disabled' : ''}`}
      onClick={p.onClick || undefined}
    >
      <span className="button__label">{p.label}</span>
    </Link>
  ) : (
    // ADD CUSTOM <Link> HTML HERE
    <ButtonSecondary
      kind="link"
      size="s"
      to={p.to}
      label={p.label}
      isDisabled={p.disabled}
      onClick={p.onClick}
    />
  )
}

// ==========================================================================
// NOTIFICATIONS
// ==========================================================================

type NotificationStatusTypes = 'error' | 'warning' | 'success'

export const FormNotification = (p: {
  message: string | JSX.Element
  options: options
  status?: NotificationStatusTypes
  extraClasses?: string
}) => {
  const alertClassName = getClassName(
    p.options,
    'formAlertClassName',
    'alert small'
  )
  const alertErrorClassName = getClassName(
    p.options,
    'formAlertErrorClassName',
    'alert-danger'
  )
  const alertWarningClassName = getClassName(
    p.options,
    'formAlertWarningClassName',
    'alert-warning'
  )
  const alertSuccessClassName = getClassName(
    p.options,
    'formAlertSuccessClassName',
    'alert-success'
  )
  const alertKindCheck =
    p.status == 'success'
      ? alertSuccessClassName
      : p.status == 'warning'
      ? alertWarningClassName
      : alertErrorClassName
  return (
    <div
      role="alert"
      className={`${alertClassName} ${
        p.status ? alertKindCheck : alertErrorClassName
      } ${p.extraClasses || ''}`}
    >
      {typeof p.message == 'string' ? (
        <div dangerouslySetInnerHTML={{ __html: p.message }} />
      ) : (
        <div> {p.message} </div>
      )}
    </div>
  )
}
